import { useCallback, useEffect, useState } from "react";

import { AttendanceEntry } from "../domain/sessionExtras";
import { SessionsAPI } from "../infrastructure/api/sessions.api";

export type SessionAttendanceState = {
    entries: AttendanceEntry[];
    loading: boolean;
    loadFailed: boolean;
    saveFailed: boolean;
    /** The member whose mark is being saved, if any. */
    savingId: string | null;
    mark: (patientId: string, attended: boolean) => Promise<void>;
};

/** Who of a group came to one of its sessions, and marking them. */
export function useSessionAttendance(sessionId: string): SessionAttendanceState {
    const [entries, setEntries] = useState<AttendanceEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [saveFailed, setSaveFailed] = useState(false);
    const [savingId, setSavingId] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setLoadFailed(false);
        SessionsAPI.getAttendance(sessionId)
            .then((items) => {
                if (!cancelled) setEntries(items);
            })
            .catch(() => {
                if (!cancelled) setLoadFailed(true);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [sessionId]);

    const mark = useCallback(
        async (patientId: string, attended: boolean) => {
            setSavingId(patientId);
            setSaveFailed(false);
            try {
                setEntries(await SessionsAPI.recordAttendance(sessionId, [{ patientId, attended }]));
            } catch {
                setSaveFailed(true);
            } finally {
                setSavingId(null);
            }
        },
        [sessionId]
    );

    return { entries, loading, loadFailed, saveFailed, savingId, mark };
}
